import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { messageDErreur, useSession } from '../auth/session';
import { cleSourceConges } from '../compte/SourceConges';
import { Alerte, Bouton, Champ } from '../ui';
import { ChampDateNaissance } from '../ui/ChampDateNaissance';

/**
 * Ajouter un enfant à un dossier existant.
 *
 * C'est ici, et non sur l'accueil, parce que c'est le geste courant : une fratrie
 * s'agrandit dans le dossier qu'on a déjà. Créer un **dossier** est le geste rare — il ne
 * sert qu'à des enfants qu'on a avec un autre parent, et le proposer au même endroit
 * conduirait des parents à ouvrir un second dossier pour un petit frère, puis à découvrir
 * qu'ils ont deux calendriers, deux messageries et deux historiques pour une seule famille.
 *
 * Le formulaire reste replié tant qu'on ne le demande pas : la fiche des enfants se
 * consulte cent fois pour un enfant ajouté une fois.
 *
 * Chacun des deux parents peut le faire, sans l'accord de l'autre. Un enfant existe ou
 * n'existe pas ; en subordonner l'inscription à une négociation reviendrait à dire qu'un
 * parent peut refuser l'existence de l'autre enfant dans le dossier.
 */
export function AjouterEnfant({ caseFileId }: { caseFileId: string }) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [naissance, setNaissance] = useState('');

  const ajouter = useMutation({
    mutationFn: () =>
      client.request(`/case-files/${caseFileId}/children`, {
        method: 'POST',
        body: { firstName: prenom.trim(), birthDate: naissance },
      }),
    onSuccess: () => {
      setPrenom('');
      setNaissance('');
      setOuvert(false);
      void queryClient.invalidateQueries({ queryKey: ['case-file', caseFileId] });
      void queryClient.invalidateQueries({ queryKey: ['case-files'] });
      // La source des congés porte la liste des enfants — un anneau et un prénom par
      // enfant au calendrier (T-064). Elle est gardée trente minutes : sans cette ligne,
      // l'enfant qu'on vient d'ajouter n'y apparaît qu'après un rechargement de page.
      void queryClient.invalidateQueries({ queryKey: cleSourceConges(caseFileId) });
    },
  });

  if (!ouvert) {
    return (
      <div className="ajouter-enfant">
        <Bouton variante="secondaire" onClick={() => setOuvert(true)}>
          <span aria-hidden="true">+</span> Ajouter un enfant
        </Bouton>
      </div>
    );
  }

  return (
    <form
      className="ajouter-enfant carte"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        ajouter.mutate();
      }}
    >
      <h3>Ajouter un enfant</h3>
      <p className="discret">
        Il rejoint ce dossier : le même calendrier, les mêmes échanges, les mêmes deux
        parents.
      </p>

      {ajouter.isError ? (
        <Alerte titre="L’enfant n’a pas pu être ajouté">{messageDErreur(ajouter.error)}</Alerte>
      ) : null}

      <Champ
        label="Prénom"
        value={prenom}
        onChange={(evenement) => setPrenom(evenement.target.value)}
        maxLength={80}
        required
      />
      <ChampDateNaissance
        label="Date de naissance"
        value={naissance}
        onChange={setNaissance}
        aide="Elle sert au calcul de son âge, jamais enregistré comme tel."
        required
      />

      <div className="ajouter-enfant-actions">
        <Bouton type="submit" disabled={!prenom.trim() || !naissance || ajouter.isPending}>
          {ajouter.isPending ? 'Ajout en cours…' : 'Ajouter'}
        </Bouton>
        <Bouton variante="secondaire" onClick={() => setOuvert(false)}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
